import React, { useState, useRef, useEffect, useCallback } from 'react';

// ============================================================
// MRVN Simple Beam Calculator - React Component
// Supports: Simple span (pin/roller) with point and uniform loads
// Units: Imperial (kip, ft, kip-ft)
// ============================================================

const COLORS = {
  primary: '#f1f5f9',
  accent: '#3b82f6',
  accentHover: '#2563eb',
  success: '#22c55e',
  warning: '#f59e0b',
  danger: '#ef4444',
  bgMain: '#0a0f1e',
  bgCard: '#0d1325',
  textDark: '#e2e8f0',
  textMid: '#94a3b8',
  textLight: '#64748b',
  border: 'rgba(255,255,255,0.1)',
  borderLight: 'rgba(255,255,255,0.06)',
};

// ============================================================
// CANVAS DRAWING FUNCTIONS
// ============================================================

function drawSupport(ctx, x, y, type) {
  ctx.fillStyle = COLORS.accent;
  ctx.strokeStyle = COLORS.primary;
  ctx.lineWidth = 2;

  if (type === 'pin') {
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x - 15, y + 30);
    ctx.lineTo(x + 15, y + 30);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(x - 20, y + 30);
    ctx.lineTo(x + 20, y + 30);
    ctx.stroke();
    for (let i = -20; i <= 20; i += 5) {
      ctx.beginPath();
      ctx.moveTo(x + i, y + 30);
      ctx.lineTo(x + i - 5, y + 40);
      ctx.stroke();
    }
  } else if (type === 'roller') {
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x - 15, y + 25);
    ctx.lineTo(x + 15, y + 25);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(x - 7, y + 30, 5, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(x + 7, y + 30, 5, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(x - 20, y + 35);
    ctx.lineTo(x + 20, y + 35);
    ctx.stroke();
    for (let i = -20; i <= 20; i += 5) {
      ctx.beginPath();
      ctx.moveTo(x + i, y + 35);
      ctx.lineTo(x + i - 5, y + 45);
      ctx.stroke();
    }
  } else if (type === 'fixed') {
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(x, y - 30);
    ctx.lineTo(x, y + 30);
    ctx.stroke();
    for (let i = -30; i <= 30; i += 8) {
      ctx.beginPath();
      ctx.moveTo(x, y + i);
      ctx.lineTo(x - 12, y + i + 8);
      ctx.stroke();
    }
  }
}

function drawPointLoad(ctx, x, y, magnitude) {
  ctx.strokeStyle = COLORS.danger;
  ctx.fillStyle = COLORS.danger;
  ctx.lineWidth = 3;

  ctx.beginPath();
  ctx.moveTo(x, y - 80);
  ctx.lineTo(x, y - 10);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(x, y - 10);
  ctx.lineTo(x - 8, y - 20);
  ctx.lineTo(x + 8, y - 20);
  ctx.closePath();
  ctx.fill();

  ctx.font = 'bold 13px "Segoe UI", sans-serif';
  ctx.fillText(`P = ${magnitude} k`, x + 10, y - 65);
}

function drawUniformLoad(ctx, x, y, length, magnitude) {
  ctx.strokeStyle = COLORS.warning;
  ctx.fillStyle = COLORS.warning;
  ctx.lineWidth = 2;

  const numArrows = Math.max(5, Math.floor(length / 80));
  const spacing = length / numArrows;

  // Top line connecting arrows
  ctx.beginPath();
  ctx.moveTo(x, y - 60);
  ctx.lineTo(x + length, y - 60);
  ctx.stroke();

  for (let i = 0; i <= numArrows; i++) {
    const arrowX = x + i * spacing;
    ctx.beginPath();
    ctx.moveTo(arrowX, y - 60);
    ctx.lineTo(arrowX, y - 10);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(arrowX, y - 10);
    ctx.lineTo(arrowX - 5, y - 18);
    ctx.lineTo(arrowX + 5, y - 18);
    ctx.closePath();
    ctx.fill();
  }

  ctx.font = 'bold 13px "Segoe UI", sans-serif';
  ctx.fillText(`w = ${magnitude} k/ft`, x + length / 2 - 35, y - 70);
}

function drawSetupCanvas(canvas, spanLength, leftSupport, rightSupport, loads) {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();

  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  ctx.scale(dpr, dpr);

  const width = rect.width;
  const height = rect.height;

  ctx.clearRect(0, 0, width, height);

  const L = spanLength || 20;
  const margin = 100;
  const scale = (width - 2 * margin) / L;
  const beamY = height / 2;
  const startX = margin;
  const endX = startX + L * scale;

  // Draw beam
  ctx.strokeStyle = COLORS.primary;
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.moveTo(startX, beamY);
  ctx.lineTo(endX, beamY);
  ctx.stroke();

  // Draw supports
  drawSupport(ctx, startX, beamY, leftSupport);
  drawSupport(ctx, endX, beamY, rightSupport);

  // Draw loads
  if (loads) {
    loads.forEach(load => {
      if (load.type === 'point' && load.magnitude && load.position !== undefined) {
        const loadX = startX + load.position * scale;
        if (loadX >= startX && loadX <= endX) {
          drawPointLoad(ctx, loadX, beamY, load.magnitude);
        }
      } else if (load.type === 'uniform' && load.magnitude) {
        drawUniformLoad(ctx, startX, beamY, L * scale, load.magnitude);
      }
    });
  }

  // Dimension line
  ctx.strokeStyle = COLORS.textLight;
  ctx.fillStyle = COLORS.textMid;
  ctx.lineWidth = 1.5;
  ctx.font = 'bold 14px "Segoe UI", sans-serif';

  const dimY = height - 25;
  ctx.beginPath();
  ctx.moveTo(startX, dimY);
  ctx.lineTo(endX, dimY);
  ctx.stroke();

  // Dimension ticks
  ctx.beginPath();
  ctx.moveTo(startX, dimY - 5);
  ctx.lineTo(startX, dimY + 5);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(endX, dimY - 5);
  ctx.lineTo(endX, dimY + 5);
  ctx.stroke();

  ctx.textAlign = 'center';
  ctx.fillText(`L = ${L} ft`, (startX + endX) / 2, dimY - 8);
  ctx.textAlign = 'left';
}

function drawResultsBeam(canvas, results, beamConfig) {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();

  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  ctx.scale(dpr, dpr);

  const width = rect.width;
  const height = rect.height;

  ctx.clearRect(0, 0, width, height);

  const L = beamConfig.spanLength;
  const margin = 100;
  const scale = (width - 2 * margin) / L;
  const beamY = height / 2 - 10;
  const startX = margin;
  const endX = startX + L * scale;

  // Beam
  ctx.strokeStyle = COLORS.primary;
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.moveTo(startX, beamY);
  ctx.lineTo(endX, beamY);
  ctx.stroke();

  // Supports
  drawSupport(ctx, startX, beamY, beamConfig.leftSupport);
  drawSupport(ctx, endX, beamY, beamConfig.rightSupport);

  // Loads
  beamConfig.loads.forEach(load => {
    if (load.type === 'point') {
      const loadX = startX + load.position * scale;
      drawPointLoad(ctx, loadX, beamY, load.magnitude);
    } else if (load.type === 'uniform') {
      drawUniformLoad(ctx, startX, beamY, L * scale, load.magnitude);
    }
  });

  // Reaction arrows
  const drawReaction = (x, value, label) => {
    ctx.strokeStyle = COLORS.success;
    ctx.fillStyle = COLORS.success;
    ctx.lineWidth = 3;

    ctx.beginPath();
    ctx.moveTo(x, beamY + 55);
    ctx.lineTo(x, beamY + 12);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(x, beamY + 12);
    ctx.lineTo(x - 6, beamY + 22);
    ctx.lineTo(x + 6, beamY + 22);
    ctx.closePath();
    ctx.fill();

    ctx.font = 'bold 12px "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`${label} = ${value.toFixed(2)} k`, x, beamY + 70);
    ctx.textAlign = 'left';
  };

  drawReaction(startX, results.R1, 'R\u2081');
  drawReaction(endX, results.R2, 'R\u2082');
}

function drawDiagram(canvas, points, maxVal, minVal, yLabel, color, symbol) {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();

  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  ctx.scale(dpr, dpr);

  const width = rect.width;
  const height = rect.height;

  ctx.clearRect(0, 0, width, height);

  const padding = { top: 40, right: 60, bottom: 50, left: 70 };
  const graphWidth = width - padding.left - padding.right;
  const graphHeight = height - padding.top - padding.bottom;

  const maxAbsVal = Math.max(Math.abs(maxVal), Math.abs(minVal)) || 1;
  const scaleFactor = graphHeight / (2 * maxAbsVal);
  const zeroY = padding.top + graphHeight / 2;

  // Grid lines
  ctx.strokeStyle = 'rgba(255,255,255,0.06)';
  ctx.lineWidth = 1;
  const numGridLines = 8;
  for (let i = 0; i <= numGridLines; i++) {
    const y = padding.top + (graphHeight / numGridLines) * i;
    ctx.beginPath();
    ctx.moveTo(padding.left, y);
    ctx.lineTo(width - padding.right, y);
    ctx.stroke();
  }

  // Axes
  ctx.strokeStyle = COLORS.textLight;
  ctx.lineWidth = 1.5;

  // X-axis (zero line)
  ctx.beginPath();
  ctx.moveTo(padding.left, zeroY);
  ctx.lineTo(width - padding.right, zeroY);
  ctx.stroke();

  // Y-axis
  ctx.beginPath();
  ctx.moveTo(padding.left, padding.top);
  ctx.lineTo(padding.left, height - padding.bottom);
  ctx.stroke();

  // Fill area
  if (points.length > 0) {
    const maxX = points[points.length - 1].x || 1;

    // Filled area
    ctx.fillStyle = color + '15';
    ctx.beginPath();
    ctx.moveTo(padding.left, zeroY);
    points.forEach(point => {
      const x = padding.left + (point.x / maxX) * graphWidth;
      const y = zeroY - point.value * scaleFactor;
      ctx.lineTo(x, y);
    });
    ctx.lineTo(width - padding.right, zeroY);
    ctx.closePath();
    ctx.fill();

    // Line
    ctx.strokeStyle = color;
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    points.forEach((point, i) => {
      const x = padding.left + (point.x / maxX) * graphWidth;
      const y = zeroY - point.value * scaleFactor;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();
  }

  // Labels
  ctx.fillStyle = COLORS.textDark;
  ctx.font = 'bold 12px "Segoe UI", sans-serif';

  // Max/min labels
  ctx.textAlign = 'right';
  ctx.fillText(`${symbol}max = ${maxVal.toFixed(2)}`, width - padding.right, padding.top - 8);
  if (Math.abs(minVal) > 0.01) {
    ctx.fillText(`${symbol}min = ${minVal.toFixed(2)}`, width - padding.right, padding.top + 8);
  }

  // Y-axis scale labels
  ctx.textAlign = 'right';
  ctx.font = '11px "Segoe UI", sans-serif';
  ctx.fillStyle = COLORS.textLight;
  const yScaleVal = maxAbsVal;
  ctx.fillText(`+${yScaleVal.toFixed(1)}`, padding.left - 8, padding.top + 4);
  ctx.fillText(`-${yScaleVal.toFixed(1)}`, padding.left - 8, height - padding.bottom + 4);
  ctx.fillText('0', padding.left - 8, zeroY + 4);

  // Y-axis label
  ctx.save();
  ctx.translate(15, height / 2);
  ctx.rotate(-Math.PI / 2);
  ctx.textAlign = 'center';
  ctx.font = 'bold 12px "Segoe UI", sans-serif';
  ctx.fillStyle = COLORS.textMid;
  ctx.fillText(yLabel, 0, 0);
  ctx.restore();

  // X-axis label
  ctx.textAlign = 'center';
  ctx.font = '12px "Segoe UI", sans-serif';
  ctx.fillStyle = COLORS.textMid;
  ctx.fillText('Position along beam (ft)', width / 2, height - 10);
  ctx.textAlign = 'left';
}

// ============================================================
// BEAM ANALYSIS ENGINE
// All calculations in consistent units: kip, ft, kip-ft
// ============================================================

function analyzeBeam(beamConfig) {
  const L = beamConfig.spanLength; // ft
  const loads = beamConfig.loads;

  let results = {
    R1: 0,
    R2: 0,
    maxShear: -Infinity,
    minShear: Infinity,
    maxMoment: -Infinity,
    minMoment: Infinity,
    shearPoints: [],
    momentPoints: [],
  };

  // Calculate reactions by summing moments about left support
  let totalMomentAboutLeft = 0; // kip-ft
  let totalVerticalLoad = 0; // kip

  loads.forEach(load => {
    if (load.type === 'uniform') {
      const w = load.magnitude; // kip/ft
      const W = w * L; // kip (total load)
      totalVerticalLoad += W;
      totalMomentAboutLeft += W * (L / 2); // acts at midspan
    } else if (load.type === 'point') {
      const P = load.magnitude; // kip
      const a = load.position; // ft from left
      totalVerticalLoad += P;
      totalMomentAboutLeft += P * a;
    }
  });

  // R2 * L = sum of moments about left support
  results.R2 = totalMomentAboutLeft / L;
  // Vertical equilibrium: R1 + R2 = total load
  results.R1 = totalVerticalLoad - results.R2;

  // Generate shear and moment at discrete points along the beam
  const numPoints = 500; // Higher resolution for smoother diagrams
  const dx = L / numPoints;

  for (let i = 0; i <= numPoints; i++) {
    const x = i * dx; // ft from left support

    // Shear at x: V(x) = R1 - sum of loads to the left of x
    let V = results.R1;
    let M = results.R1 * x;

    loads.forEach(load => {
      if (load.type === 'uniform') {
        V -= load.magnitude * x;
        M -= (load.magnitude * x * x) / 2;
      } else if (load.type === 'point') {
        if (x > load.position) {
          V -= load.magnitude;
          M -= load.magnitude * (x - load.position);
        }
      }
    });

    results.shearPoints.push({ x, value: V });
    results.momentPoints.push({ x, value: M });

    results.maxShear = Math.max(results.maxShear, V);
    results.minShear = Math.min(results.minShear, V);
    results.maxMoment = Math.max(results.maxMoment, M);
    results.minMoment = Math.min(results.minMoment, M);
  }

  return results;
}

// ============================================================
// INPUT VALIDATION
// ============================================================

function validateInputs(beamConfig) {
  const errors = [];

  if (!beamConfig.spanLength || beamConfig.spanLength <= 0) {
    errors.push('Span length must be greater than zero.');
  }

  if (beamConfig.loads.length === 0) {
    errors.push('At least one load is required.');
  }

  beamConfig.loads.forEach((load, i) => {
    const num = i + 1;
    if (!load.magnitude || load.magnitude <= 0) {
      errors.push(`Load ${num}: Magnitude must be greater than zero.`);
    }
    if (load.type === 'point') {
      if (load.position === undefined || load.position === null || load.position === '') {
        errors.push(`Load ${num}: Position is required for point loads.`);
      } else if (load.position < 0) {
        errors.push(`Load ${num}: Position cannot be negative.`);
      } else if (load.position > beamConfig.spanLength) {
        errors.push(`Load ${num}: Position (${load.position} ft) exceeds span length (${beamConfig.spanLength} ft).`);
      }
    }
  });

  return errors;
}

// ============================================================
// STYLES
// ============================================================

const styles = {
  container: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '2rem',
    fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
    color: COLORS.textDark,
    lineHeight: 1.6,
    minHeight: '100vh',
    background: '#0a0f1e',
  },
  header: {
    background: '#080c18',
    borderBottom: '1px solid rgba(30,58,95,0.2)',
    color: 'white',
    padding: '1.25rem 2rem',
    borderRadius: '8px 8px 0 0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  headerTitle: {
    fontSize: '1.4rem',
    fontWeight: 700,
    margin: 0,
  },
  headerSub: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: '0.85rem',
    margin: 0,
  },
  backBtn: {
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    padding: '0.5rem 1.25rem',
    borderRadius: '6px',
    color: 'white',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '0.9rem',
    transition: 'background 0.2s',
  },
  body: {
    background: '#0a0f1e',
    border: `1px solid rgba(255,255,255,0.06)`,
    borderTop: 'none',
    borderRadius: '0 0 8px 8px',
    padding: '1.5rem 2rem 2rem',
  },
  tabs: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1.5rem',
    borderBottom: `2px solid ${COLORS.borderLight}`,
    paddingBottom: '0',
  },
  tab: (active) => ({
    padding: '0.75rem 1.5rem',
    background: 'none',
    border: 'none',
    borderBottom: active ? `3px solid ${COLORS.accent}` : '3px solid transparent',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: active ? 700 : 500,
    color: active ? COLORS.accent : COLORS.textMid,
    transition: 'all 0.2s',
    marginBottom: '-2px',
  }),
  infoBox: {
    background: 'rgba(30,58,95,0.15)',
    borderLeft: `4px solid ${COLORS.accent}`,
    padding: '0.75rem 1rem',
    marginBottom: '1.5rem',
    borderRadius: '0 4px 4px 0',
    fontSize: '0.9rem',
    color: COLORS.textMid,
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1.25rem',
    marginBottom: '1.5rem',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontWeight: 600,
    marginBottom: '0.35rem',
    fontSize: '0.85rem',
    color: COLORS.textMid,
    letterSpacing: '0.02em',
  },
  input: {
    padding: '0.6rem 0.75rem',
    border: `1.5px solid ${COLORS.border}`,
    borderRadius: '4px',
    fontSize: '0.95rem',
    outline: 'none',
    transition: 'border-color 0.2s',
    fontFamily: 'inherit',
    background: 'rgba(255,255,255,0.04)',
    color: '#e2e8f0',
  },
  select: {
    padding: '0.6rem 0.75rem',
    border: `1.5px solid ${COLORS.border}`,
    borderRadius: '4px',
    fontSize: '0.95rem',
    outline: 'none',
    background: 'rgba(255,255,255,0.04)',
    color: '#e2e8f0',
    fontFamily: 'inherit',
  },
  canvas: {
    width: '100%',
    height: '220px',
    borderRadius: '6px',
    border: `1px solid ${COLORS.borderLight}`,
    background: '#0d1325',
  },
  diagramCanvas: {
    width: '100%',
    height: '280px',
    borderRadius: '6px',
    border: `1px solid ${COLORS.borderLight}`,
    background: '#0d1325',
  },
  loadEntry: {
    background: 'rgba(255,255,255,0.03)',
    border: `1.5px solid ${COLORS.borderLight}`,
    borderRadius: '6px',
    padding: '1.25rem',
    marginBottom: '0.75rem',
  },
  loadEntryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  btn: (variant) => {
    const base = {
      padding: '0.6rem 1.5rem',
      border: 'none',
      borderRadius: '4px',
      fontSize: '0.9rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s',
      fontFamily: 'inherit',
    };
    switch (variant) {
      case 'primary':
        return { ...base, background: COLORS.accent, color: 'white' };
      case 'success':
        return { ...base, background: COLORS.success, color: 'white' };
      case 'danger':
        return { ...base, background: COLORS.danger, color: 'white', padding: '0.4rem 0.75rem', fontSize: '0.8rem' };
      case 'add':
        return { ...base, background: COLORS.success, color: 'white', padding: '0.5rem 1.25rem', fontSize: '0.85rem' };
      default:
        return base;
    }
  },
  navButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '2rem',
    paddingTop: '1.5rem',
    borderTop: `1px solid ${COLORS.borderLight}`,
  },
  resultsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '0.75rem',
    marginBottom: '1.5rem',
  },
  resultCard: (gradient) => ({
    background: gradient,
    color: 'white',
    padding: '1.25rem',
    borderRadius: '6px',
    textAlign: 'center',
  }),
  resultLabel: {
    fontSize: '0.8rem',
    opacity: 0.9,
    marginBottom: '0.25rem',
    fontWeight: 500,
  },
  resultValue: {
    fontSize: '1.5rem',
    fontWeight: 700,
    letterSpacing: '-0.02em',
  },
  resultUnit: {
    fontSize: '0.8rem',
    opacity: 0.85,
    marginTop: '0.15rem',
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: COLORS.textDark,
    marginBottom: '0.5rem',
    marginTop: '1.5rem',
  },
  errorBox: {
    background: 'rgba(239,68,68,0.08)',
    borderLeft: `4px solid ${COLORS.danger}`,
    padding: '0.75rem 1rem',
    marginBottom: '1rem',
    borderRadius: '0 4px 4px 0',
    fontSize: '0.9rem',
    color: COLORS.danger,
  },
};

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function BeamCalculator({ onBack }) {
  const [activeTab, setActiveTab] = useState(0);
  const [spanLength, setSpanLength] = useState(20);
  const [leftSupport, setLeftSupport] = useState('pin');
  const [rightSupport, setRightSupport] = useState('roller');
  const [loads, setLoads] = useState([]);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState([]);
  const [projectName, setProjectName] = useState('');
  const [engineerName, setEngineerName] = useState('');
  const [projectNumber, setProjectNumber] = useState('');

  const setupCanvasRef = useRef(null);
  const beamVisCanvasRef = useRef(null);
  const shearCanvasRef = useRef(null);
  const momentCanvasRef = useRef(null);

  // Redraw setup canvas when inputs change
  const redrawSetup = useCallback(() => {
    drawSetupCanvas(setupCanvasRef.current, spanLength, leftSupport, rightSupport, loads);
  }, [spanLength, leftSupport, rightSupport, loads]);

  useEffect(() => {
    if (activeTab === 0) {
      // Small delay to ensure canvas is mounted
      const timer = setTimeout(redrawSetup, 50);
      return () => clearTimeout(timer);
    }
  }, [activeTab, redrawSetup]);

  // Redraw results diagrams when results change
  useEffect(() => {
    if (activeTab === 2 && results) {
      const timer = setTimeout(() => {
        const config = { spanLength, leftSupport, rightSupport, loads };
        drawResultsBeam(beamVisCanvasRef.current, results, config);
        drawDiagram(shearCanvasRef.current, results.shearPoints, results.maxShear, results.minShear, 'Shear (kip)', COLORS.danger, 'V');
        drawDiagram(momentCanvasRef.current, results.momentPoints, results.maxMoment, results.minMoment, 'Moment (kip-ft)', COLORS.success, 'M');
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [activeTab, results, spanLength, leftSupport, rightSupport, loads]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (activeTab === 0) redrawSetup();
      if (activeTab === 2 && results) {
        const config = { spanLength, leftSupport, rightSupport, loads };
        drawResultsBeam(beamVisCanvasRef.current, results, config);
        drawDiagram(shearCanvasRef.current, results.shearPoints, results.maxShear, results.minShear, 'Shear (kip)', COLORS.danger, 'V');
        drawDiagram(momentCanvasRef.current, results.momentPoints, results.maxMoment, results.minMoment, 'Moment (kip-ft)', COLORS.success, 'M');
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [activeTab, results, redrawSetup, spanLength, leftSupport, rightSupport, loads]);

  const addLoad = () => {
    setLoads(prev => [...prev, {
      id: Date.now(),
      type: 'point',
      magnitude: 10,
      position: spanLength / 2,
    }]);
  };

  const removeLoad = (id) => {
    setLoads(prev => prev.filter(l => l.id !== id));
  };

  const updateLoad = (id, field, value) => {
    setLoads(prev => prev.map(l => {
      if (l.id !== id) return l;
      if (field === 'type') return { ...l, [field]: value };
      if (value === '' || value === '-') return { ...l, [field]: '' };
      const num = parseFloat(value);
      return { ...l, [field]: isNaN(num) ? l[field] : num };
    }));
  };

  const calculate = () => {
    const config = { spanLength, leftSupport, rightSupport, loads };
    const validationErrors = validateInputs(config);

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors([]);
    const analysisResults = analyzeBeam(config);
    setResults(analysisResults);
    setActiveTab(2);
  };

  // ============================================================
  // PDF GENERATION
  // ============================================================

  const loadJsPDF = () => {
    return new Promise((resolve, reject) => {
      if (window.jspdf) { resolve(window.jspdf.jsPDF); return; }
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.2/jspdf.umd.min.js';
      script.onload = () => resolve(window.jspdf.jsPDF);
      script.onerror = () => reject(new Error('Failed to load jsPDF'));
      document.head.appendChild(script);
    });
  };

  const generatePDF = async () => {
    if (!results) return;

    let jsPDF;
    try {
      jsPDF = await loadJsPDF();
    } catch (err) {
      alert('Failed to load PDF library. Check your internet connection.');
      return;
    }

    const doc = new jsPDF('p', 'pt', 'letter');
    const W = 612;
    const H = 792;
    const M = { left: 54, right: 54, top: 54, bottom: 54 };
    const CW = W - M.left - M.right;
    let y = M.top;
    const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    let pageNum = 1;

    // --- Colors ---
    const navy = [30, 58, 95];
    const darkNavy = [15, 36, 64];
    const blue = [59, 130, 246];
    const textDark = [30, 41, 59];
    const textMid = [71, 85, 105];
    const textLight = [148, 163, 184];
    const borderColor = [203, 213, 225];
    const bgLight = [241, 245, 249];

    // --- Helper Functions ---
    const drawHeader = () => {
      // Navy header bar
      doc.setFillColor(...navy);
      doc.rect(0, 0, W, 72, 'F');
      // Logo M
      doc.setFillColor(...darkNavy);
      doc.roundedRect(M.left, 16, 40, 40, 6, 6, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('M', M.left + 13, 43);
      // MRVN text
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('MRVN', M.left + 50, 35);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(180, 200, 220);
      doc.text('Structural Engineering Platform', M.left + 50, 48);
      // Right side - date
      doc.setTextColor(180, 200, 220);
      doc.setFontSize(8);
      doc.text(today, W - M.right, 30, { align: 'right' });
      if (projectNumber) {
        doc.text('Project: ' + projectNumber, W - M.right, 42, { align: 'right' });
      }
      doc.text('Page ' + pageNum, W - M.right, 54, { align: 'right' });
    };

    const drawFooter = () => {
      doc.setDrawColor(...borderColor);
      doc.setLineWidth(0.5);
      doc.line(M.left, H - 40, W - M.right, H - 40);
      doc.setFontSize(7);
      doc.setTextColor(...textLight);
      doc.text('Generated by MRVN \u2014 mrvnai.com', M.left, H - 28);
      doc.text('Page ' + pageNum, W - M.right, H - 28, { align: 'right' });
      if (engineerName) {
        doc.text('Engineer: ' + engineerName, W / 2, H - 28, { align: 'center' });
      }
    };

    const drawSectionTitle = (title, yPos) => {
      doc.setFillColor(...bgLight);
      doc.rect(M.left, yPos, CW, 22, 'F');
      doc.setDrawColor(...borderColor);
      doc.setLineWidth(0.5);
      doc.line(M.left, yPos, M.left, yPos + 22);
      doc.setFillColor(...blue);
      doc.rect(M.left, yPos, 3, 22, 'F');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...textDark);
      doc.text(title, M.left + 12, yPos + 15);
      return yPos + 32;
    };

    const checkPageBreak = (needed) => {
      if (y + needed > H - M.bottom - 50) {
        drawFooter();
        doc.addPage();
        pageNum++;
        drawHeader();
        y = 90;
      }
    };

    // ===========================================
    // PAGE 1
    // ===========================================
    drawHeader();
    y = 86;

    // Title block
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...textDark);
    doc.text('Simple Beam Analysis', M.left, y);
    y += 16;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...textMid);
    doc.text('Static analysis \u2022 Pin/roller supports \u2022 Imperial units (kip, ft)', M.left, y);
    y += 10;
    if (projectName) {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...textDark);
      doc.text('Project: ' + projectName, M.left, y);
      y += 12;
    }
    y += 8;

    // --- Beam Configuration ---
    y = drawSectionTitle('Beam Configuration', y);

    const col1 = M.left + 10;
    const col2 = M.left + 130;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...textMid);
    doc.text('Span Length:', col1, y);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...textDark);
    doc.text(spanLength + ' ft', col2, y);
    y += 16;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...textMid);
    doc.text('Left Support:', col1, y);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...textDark);
    doc.text(leftSupport.charAt(0).toUpperCase() + leftSupport.slice(1), col2, y);
    y += 16;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...textMid);
    doc.text('Right Support:', col1, y);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...textDark);
    doc.text(rightSupport.charAt(0).toUpperCase() + rightSupport.slice(1), col2, y);
    y += 24;

    // --- Applied Loads ---
    y = drawSectionTitle('Applied Loads', y);

    // Table header
    doc.setFillColor(240, 243, 248);
    doc.rect(M.left, y, CW, 18, 'F');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...textMid);
    const loadCols = [M.left + 10, M.left + 80, M.left + 200, M.left + 340];
    doc.text('Load #', loadCols[0], y + 12);
    doc.text('Type', loadCols[1], y + 12);
    doc.text('Magnitude', loadCols[2], y + 12);
    doc.text('Position', loadCols[3], y + 12);
    y += 22;

    loads.forEach((load, i) => {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...textDark);
      doc.setFontSize(9);
      doc.text(String(i + 1), loadCols[0], y + 4);
      doc.text(load.type === 'point' ? 'Point Load' : 'Uniform Load', loadCols[1], y + 4);
      doc.text(load.magnitude + (load.type === 'point' ? ' kip' : ' kip/ft'), loadCols[2], y + 4);
      doc.text(load.type === 'point' ? load.position + ' ft from left' : 'Full span', loadCols[3], y + 4);
      // Row separator
      doc.setDrawColor(235, 238, 242);
      doc.setLineWidth(0.3);
      doc.line(M.left, y + 10, W - M.right, y + 10);
      y += 18;
    });
    y += 10;

    // --- Beam Diagram ---
    checkPageBreak(200);
    y = drawSectionTitle('Beam Diagram', y);

    if (beamVisCanvasRef.current) {
      try {
        const beamImg = beamVisCanvasRef.current.toDataURL('image/png');
        const imgW = CW;
        const imgH = (beamVisCanvasRef.current.height / beamVisCanvasRef.current.width) * imgW;
        const dH = Math.min(imgH, 140);
        doc.setFillColor(13, 19, 37);
        doc.roundedRect(M.left, y, imgW, dH, 4, 4, 'F');
        doc.addImage(beamImg, 'PNG', M.left, y, imgW, dH);
        y += dH + 12;
      } catch (e) { y += 10; }
    }

    // --- Results Summary ---
    checkPageBreak(120);
    y = drawSectionTitle('Analysis Results', y);

    // Results table
    const resData = [
      ['Left Reaction (R\u2081)', results.R1.toFixed(2) + ' kip'],
      ['Right Reaction (R\u2082)', results.R2.toFixed(2) + ' kip'],
      ['Maximum Shear', results.maxShear.toFixed(2) + ' kip'],
      ['Minimum Shear', results.minShear.toFixed(2) + ' kip'],
      ['Maximum Moment', results.maxMoment.toFixed(2) + ' kip-ft'],
    ];

    resData.forEach((row, i) => {
      if (i % 2 === 0) {
        doc.setFillColor(248, 250, 252);
        doc.rect(M.left, y - 4, CW, 18, 'F');
      }
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...textMid);
      doc.text(row[0], col1, y + 8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...textDark);
      doc.text(row[1], M.left + 280, y + 8);
      y += 18;
    });
    y += 12;

    // --- Shear Force Diagram ---
    checkPageBreak(220);
    y = drawSectionTitle('Shear Force Diagram', y);

    if (shearCanvasRef.current) {
      try {
        const shearImg = shearCanvasRef.current.toDataURL('image/png');
        const imgW = CW;
        const imgH = (shearCanvasRef.current.height / shearCanvasRef.current.width) * imgW;
        doc.addImage(shearImg, 'PNG', M.left, y, imgW, Math.min(imgH, 180));
        y += Math.min(imgH, 180) + 12;
      } catch (e) { y += 10; }
    }

    // --- Bending Moment Diagram ---
    checkPageBreak(220);
    y = drawSectionTitle('Bending Moment Diagram', y);

    if (momentCanvasRef.current) {
      try {
        const momentImg = momentCanvasRef.current.toDataURL('image/png');
        const imgW = CW;
        const imgH = (momentCanvasRef.current.height / momentCanvasRef.current.width) * imgW;
        doc.addImage(momentImg, 'PNG', M.left, y, imgW, Math.min(imgH, 180));
        y += Math.min(imgH, 180) + 12;
      } catch (e) { y += 10; }
    }

    drawFooter();

    // Save
    const filename = projectName
      ? projectName.replace(/[^a-zA-Z0-9]/g, '_') + '_Beam_Analysis.pdf'
      : 'Beam_Analysis.pdf';
    doc.save(filename);
  };

  const tabNames = ['Beam Setup', 'Loads', 'Results'];

  return (
    <div style={{ background: '#0a0f1e', minHeight: '100vh' }}>
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.headerTitle}>Simple Beam Calculator</h1>
          <p style={styles.headerSub}>Static analysis &bull; Pin/roller supports &bull; Imperial units (kip, ft)</p>
        </div>
        <button
          style={styles.backBtn}
          onClick={onBack}
          onMouseEnter={e => e.target.style.background = 'rgba(255,255,255,0.25)'}
          onMouseLeave={e => e.target.style.background = 'rgba(255,255,255,0.15)'}
        >
          &larr; Back to Dashboard
        </button>
      </div>

      <div style={styles.body}>
        {/* Tabs */}
        <div style={styles.tabs}>
          {tabNames.map((name, i) => (
            <button
              key={i}
              style={styles.tab(activeTab === i)}
              onClick={() => setActiveTab(i)}
            >
              {name}
            </button>
          ))}
        </div>

        {/* Error display */}
        {errors.length > 0 && (
          <div style={styles.errorBox}>
            {errors.map((err, i) => (
              <div key={i}>{err}</div>
            ))}
          </div>
        )}

        {/* Tab 0: Beam Setup */}
        {activeTab === 0 && (
          <div>
            <div style={styles.infoBox}>
              <strong>Simple Beam:</strong> One span with two supports &nbsp;|&nbsp; <strong>Units:</strong> Imperial (kip, ft)
            </div>

            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Span Length (ft)</label>
                <input
                  type="number"
                  style={styles.input}
                  value={spanLength}
                  min="0.1"
                  step="0.5"
                  onChange={e => {
                    const val = e.target.value;
                    if (val === '' || val === '-') { setSpanLength(''); return; }
                    const num = parseFloat(val);
                    if (!isNaN(num)) setSpanLength(num);
                  }}
                  onBlur={e => { if (spanLength === '' || spanLength === 0) setSpanLength(20); e.target.style.borderColor = COLORS.border; }}
                  onFocus={e => e.target.style.borderColor = COLORS.accent}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Left Support</label>
                <select
                  style={styles.select}
                  value={leftSupport}
                  onChange={e => setLeftSupport(e.target.value)}
                >
                  <option value="pin">Pin</option>
                  <option value="roller">Roller</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Right Support</label>
                <select
                  style={styles.select}
                  value={rightSupport}
                  onChange={e => setRightSupport(e.target.value)}
                >
                  <option value="pin">Pin</option>
                  <option value="roller">Roller</option>
                </select>
              </div>
            </div>

            <canvas ref={setupCanvasRef} style={styles.canvas} />

            <div style={styles.navButtons}>
              <div />
              <button style={styles.btn('primary')} onClick={() => setActiveTab(1)}>
                Next: Add Loads &rarr;
              </button>
            </div>
          </div>
        )}

        {/* Tab 1: Loads */}
        {activeTab === 1 && (
          <div>
            <div style={styles.infoBox}>
              Add point loads and/or distributed loads. All loads act downward (gravity direction).
            </div>

            {loads.length === 0 && (
              <div style={{ textAlign: 'center', padding: '2rem', color: COLORS.textLight }}>
                No loads added yet. Click the button below to add your first load.
              </div>
            )}

            {loads.map((load, index) => (
              <div key={load.id} style={styles.loadEntry}>
                <div style={styles.loadEntryHeader}>
                  <strong style={{ fontSize: '0.9rem', color: COLORS.textDark }}>Load {index + 1}</strong>
                  <button style={styles.btn('danger')} onClick={() => removeLoad(load.id)}>Remove</button>
                </div>
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Load Type</label>
                    <select
                      style={styles.select}
                      value={load.type}
                      onChange={e => updateLoad(load.id, 'type', e.target.value)}
                    >
                      <option value="point">Point Load (P)</option>
                      <option value="uniform">Uniform Load (w)</option>
                    </select>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      Magnitude ({load.type === 'point' ? 'kip' : 'kip/ft'})
                    </label>
                    <input
                      type="number"
                      style={styles.input}
                      value={load.magnitude}
                      min="0"
                      step="0.5"
                      onChange={e => updateLoad(load.id, 'magnitude', e.target.value)}
                      onFocus={e => e.target.style.borderColor = COLORS.accent}
                      onBlur={e => e.target.style.borderColor = COLORS.border}
                    />
                  </div>
                  {load.type === 'point' && (
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Position from left (ft)</label>
                      <input
                        type="number"
                        style={styles.input}
                        value={load.position}
                        min="0"
                        max={spanLength}
                        step="0.5"
                        onChange={e => updateLoad(load.id, 'position', e.target.value)}
                        onFocus={e => e.target.style.borderColor = COLORS.accent}
                        onBlur={e => e.target.style.borderColor = COLORS.border}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}

            <button style={styles.btn('add')} onClick={addLoad}>+ Add Load</button>

            <div style={styles.navButtons}>
              <button style={styles.btn('primary')} onClick={() => setActiveTab(0)}>
                &larr; Beam Setup
              </button>
              <button style={styles.btn('success')} onClick={calculate}>
                Calculate Results &rarr;
              </button>
            </div>
          </div>
        )}

        {/* Tab 2: Results */}
        {activeTab === 2 && (
          <div>
            {!results ? (
              <div style={styles.infoBox}>
                Add loads and click "Calculate Results" to see analysis output.
              </div>
            ) : (
              <>
                <h3 style={styles.sectionTitle}>Reactions</h3>
                <div style={styles.resultsGrid}>
                  <div style={styles.resultCard('linear-gradient(135deg, #4f46e5, #6366f1)')}>
                    <div style={styles.resultLabel}>Left Reaction (R&#8321;)</div>
                    <div style={styles.resultValue}>{results.R1.toFixed(2)}</div>
                    <div style={styles.resultUnit}>kip</div>
                  </div>
                  <div style={styles.resultCard('linear-gradient(135deg, #4f46e5, #6366f1)')}>
                    <div style={styles.resultLabel}>Right Reaction (R&#8322;)</div>
                    <div style={styles.resultValue}>{results.R2.toFixed(2)}</div>
                    <div style={styles.resultUnit}>kip</div>
                  </div>
                </div>

                <h3 style={styles.sectionTitle}>Critical Values</h3>
                <div style={styles.resultsGrid}>
                  <div style={styles.resultCard('linear-gradient(135deg, #dc2626, #ef4444)')}>
                    <div style={styles.resultLabel}>Max Shear</div>
                    <div style={styles.resultValue}>{results.maxShear.toFixed(2)}</div>
                    <div style={styles.resultUnit}>kip</div>
                  </div>
                  <div style={styles.resultCard('linear-gradient(135deg, #2563eb, #3b82f6)')}>
                    <div style={styles.resultLabel}>Min Shear</div>
                    <div style={styles.resultValue}>{results.minShear.toFixed(2)}</div>
                    <div style={styles.resultUnit}>kip</div>
                  </div>
                  <div style={styles.resultCard('linear-gradient(135deg, #16a34a, #22c55e)')}>
                    <div style={styles.resultLabel}>Max Moment</div>
                    <div style={styles.resultValue}>{results.maxMoment.toFixed(2)}</div>
                    <div style={styles.resultUnit}>kip-ft</div>
                  </div>
                </div>

                <h3 style={styles.sectionTitle}>Beam Diagram</h3>
                <canvas ref={beamVisCanvasRef} style={styles.canvas} />

                <h3 style={styles.sectionTitle}>Shear Force Diagram</h3>
                <canvas ref={shearCanvasRef} style={styles.diagramCanvas} />

                <h3 style={styles.sectionTitle}>Bending Moment Diagram</h3>
                <canvas ref={momentCanvasRef} style={styles.diagramCanvas} />

                <div style={{ marginTop: '1.5rem', padding: '1.25rem', background: 'rgba(30,58,95,0.1)', border: '1px solid rgba(30,58,95,0.25)', borderRadius: '8px' }}>
                  <h3 style={{ ...styles.sectionTitle, marginTop: 0 }}>Calculation Package</h3>
                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                    <div style={{ flex: '1 1 200px' }}>
                      <label style={styles.label}>Project Name</label>
                      <input type="text" style={styles.input} value={projectName} onChange={e => setProjectName(e.target.value)} placeholder="Enter project name" />
                    </div>
                    <div style={{ flex: '1 1 200px' }}>
                      <label style={styles.label}>Engineer</label>
                      <input type="text" style={styles.input} value={engineerName} onChange={e => setEngineerName(e.target.value)} placeholder="Enter engineer name" />
                    </div>
                    <div style={{ flex: '1 1 150px' }}>
                      <label style={styles.label}>Project Number</label>
                      <input type="text" style={styles.input} value={projectNumber} onChange={e => setProjectNumber(e.target.value)} placeholder="Optional" />
                    </div>
                  </div>
                  <button style={{ ...styles.btn('primary'), background: '#1e3a5f', display: 'flex', alignItems: 'center', gap: '0.5rem' }} onClick={generatePDF}>
                    Export Calculation Package (PDF)
                  </button>
                </div>

                <div style={styles.navButtons}>
                  <button style={styles.btn('primary')} onClick={() => setActiveTab(1)}>
                    &larr; Edit Loads
                  </button>
                  <button style={styles.btn('success')} onClick={calculate}>
                    Recalculate
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
    </div>
  );
}
